import type { Provenance } from './schedule.ts';

export const FRAME_RATE = 48000;
export const MAX_FADE_FRAMES = 12000;
export const MAX_OPS = 8192;
export const PROTOCOL_VERSION = 1;
export const MAX_ENVELOPE_BYTES = 4 * 1024 * 1024;
export const MAX_OP_FRAME_COUNT = 2147483647;

export interface NativePartAsset {
  assetId: string;
  kind: 'part';
  path: string;
  pcmTotalFrames: number;
  byteLength: number;
}

export interface NativeProfileAsset {
  assetId: string;
  kind: 'profile';
  path: string;
  byteLength: number;
}

export type NativeAsset = NativePartAsset | NativeProfileAsset;

export type NativeSource =
  | { kind: 'speech'; assetId: string; offsetFrame: number }
  | { kind: 'tone'; assetId: string; seed: number; targetRms: number }
  | { kind: 'silence' };

export interface NativeOp {
  atFrame: number;
  frameCount: number;
  fadeInFrames: number;
  fadeOutFrames: number;
  source: NativeSource;
  prov: Provenance;
  reconciledFrames: number;
}

export interface NativeEnvelope {
  protocolVersion: typeof PROTOCOL_VERSION;
  sampleRate: typeof FRAME_RATE;
  recordingId: string;
  manifestVersion: string;
  toneStrategy: 'synth' | 'inaudible';
  totalFrameCount: number;
  assets: NativeAsset[];
  ops: NativeOp[];
}

export function secondsToFrame(sec: number): number {
  return Math.floor(sec * FRAME_RATE + 0.5);
}

export function assertNever(value: never): never {
  throw new Error(`unknown native protocol kind: ${JSON.stringify(value)}`);
}

export function sourceAssetKind(source: NativeSource): NativeAsset['kind'] | null {
  switch (source.kind) {
    case 'speech': return 'part';
    case 'tone': return 'profile';
    case 'silence': return null;
    default: return assertNever(source);
  }
}

export function assetFrameLimit(asset: NativeAsset): number | null {
  switch (asset.kind) {
    case 'part': return asset.pcmTotalFrames;
    case 'profile': return null;
    default: return assertNever(asset);
  }
}
